import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  ZDrawScheduleSelect,
  ZDrawScheduleSelectAdmin,
  ZDrawSelectAdmin,
  ZDrawTypePlanSelect,
  ZDrawTypeSelect,
} from './models';

import { ZDrawTypeFullResult } from '@daymade/common-lib/dbJsonModels';
import { zodInsertDraw } from '../../generated/zodSchemas.generated';
import { ZPrizeSelect } from '../../prize';
import { booleanParamSchema, successSchema } from '../../utils';

const c = initContract();
export const adminDrawContract = c.router(
  {
    getDrawsByGroup: {
      method: 'GET',
      path: '/draw/group/:group',
      pathParams: z.object({
        group: z.string(),
      }),
      query: z.object({
        includeCompleted: z.optional(booleanParamSchema),
        excludeScheduled: z.optional(booleanParamSchema),
        limit: z.optional(z.coerce.number()),
        offset: z.optional(z.coerce.number()),
      }),
      responses: {
        200: z.object({
          draws: z.array(
            z.object({
              draw: ZDrawSelectAdmin,
              draw_schedule: ZDrawScheduleSelectAdmin,
              draw_type: ZDrawTypeSelect,
            }),
          ),
        }),
      },
    },
    getDraw: {
      method: 'GET',
      path: '/draw/:id',
      pathParams: z.object({
        id: z.coerce.number(),
      }),
      responses: {
        200: z.object({
          draw: z.object({
            draw: ZDrawSelectAdmin,
            draw_schedule: ZDrawScheduleSelectAdmin,
            draw_type: ZDrawTypeSelect,
          }),
        }),
      },
    },

    verificationComplete: {
      method: 'POST',
      path: '/draw/:draw_id/verification-complete',
      pathParams: z.object({
        draw_id: z.coerce.number(),
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          draw: z.object({
            draw: ZDrawSelectAdmin,
            draw_schedule: ZDrawScheduleSelectAdmin,
            draw_type: ZDrawTypeSelect,
          }),
        }),
      },
    },

    setDrawResults: {
      method: 'POST',
      path: '/draw/:draw_id/results',
      pathParams: z.object({
        draw_id: z.coerce.number(),
      }),
      body: z.object({
        results: ZDrawTypeFullResult,
      }),
      responses: {
        200: z.object({
          draw: z.object({
            draw: ZDrawSelectAdmin,
            draw_schedule: ZDrawScheduleSelectAdmin,
            draw_type: ZDrawTypeSelect,
          }),
        }),
      },
    },
    autoGenerateResults: {
      method: 'POST',
      path: '/draw/:draw_id/auto-generate-results',
      pathParams: z.object({
        draw_id: z.coerce.number(),
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          draw: z.object({
            draw: ZDrawSelectAdmin,
            draw_schedule: ZDrawScheduleSelectAdmin,
            draw_type: ZDrawTypeSelect,
          }),
        }),
      },
    },

    awardAndNotifyPrizes: {
      method: 'POST',
      path: '/draw/:draw_id/award-and-notify-prizes',
      pathParams: z.object({
        draw_id: z.coerce.number(),
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          queued: z.boolean(),
          draw: z.object({
            draw: ZDrawSelectAdmin,
            draw_schedule: ZDrawScheduleSelectAdmin,
            draw_type: ZDrawTypeSelect,
          }),
        }),
      },
    },
    simulateWinners: {
      method: 'POST',
      path: '/draw/:draw_id/simulate-winners',
      pathParams: z.object({
        draw_id: z.coerce.number(),
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          winners: z.record(
            z.string(),
            z.object({ code: z.string(), total: z.string() }),
          ),
        }),
      },
    },
    getDrawSchedules: {
      method: 'GET',
      path: '/draw-schedules',
      query: z.object({
        allowManualDrawCreation: booleanParamSchema.optional(),
      }),
      responses: {
        200: z.object({
          drawSchedules: ZDrawScheduleSelect.extend({
            drawType: ZDrawTypeSelect,
            drawTypePlans: ZDrawTypePlanSelect.array(),
            nextDraw: ZDrawSelectAdmin,
          }).array(),
        }),
      },
    },
    getBoostedEntriesForDraw: {
      method: 'GET',
      path: '/draw/:draw_id/boosted-entries',
      pathParams: z.object({
        draw_id: z.coerce.number(),
      }),
      responses: {
        200: z.object({
          boostedEntries: z.array(
            z.object({
              entry_id: z.string(),
              purchase_date: z.date(),
              numbers: z.string(),
            }),
          ),
        }),
      },
    },
    createDrawForDrawSchedule: {
      method: 'POST',
      path: '/draw-schedule/:draw_schedule_id/create-draw',
      pathParams: z.object({
        draw_schedule_id: z.coerce.number(),
      }),
      body: z.object({
        prizeId: z.number().optional(),
      }),
      responses: {
        200: ZDrawSelectAdmin,
      },
    },
    updateDraw: {
      method: 'POST',
      path: '/draw/:draw_id/update',
      pathParams: z.object({
        draw_id: z.coerce.number(),
      }),
      body: zodInsertDraw
        .pick({
          prizeId: true,
        })
        .partial(),
      responses: {
        200: successSchema,
      },
    },
    getLatestDrawsOfDrawSchedule: {
      method: 'GET',
      path: '/draw-schedule/:draw_schedule_id/latest-draws',
      pathParams: z.object({
        draw_schedule_id: z.coerce.number(),
      }),
      query: z.object({
        limit: z.coerce.number().optional(),
      }),
      responses: {
        200: z.object({
          draws: z.array(
            z.object({
              draw: ZDrawSelectAdmin,
              prize: ZPrizeSelect.nullable(),
            }),
          ),
        }),
      },
    },
  },
  {
    pathPrefix: '/admin',
    strictStatusCodes: true,
    commonResponses: {
      401: c.type<{ message: string; statusCode: 401 }>(),
      403: c.type<{ message: string; statusCode: 403 }>(),
    },
  },
);
